import {
  Database,
  Mail,
  MessageCircle,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import type { ReactNode } from "react";

const CONTACT_EMAIL = "biramacedo@gmail.com";

function PolicySection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
      <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-950 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <header className="rounded-3xl bg-slate-950 px-6 py-8 text-white shadow-lg sm:px-9 sm:py-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
            Beleza e Bronze
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            Política de Privacidade
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
            Esta política explica como a plataforma Beleza e Bronze trata dados
            para fornecer agenda, atendimento, loja e integrações autorizadas
            pelos salões.
          </p>
          <p className="mt-4 text-xs text-slate-400">
            Última atualização: 12 de outubro de 2026
          </p>
        </header>

        <div className="mt-6 space-y-4">
          <PolicySection title="Quem usa a plataforma e quais dados podem ser tratados">
            <p>
              A Beleza e Bronze é uma plataforma de gestão para salões. Cada
              salão administra seus próprios dados e sua própria equipe. A
              plataforma pode tratar dados de cadastro e contato de
              administradores, profissionais e clientes, além de dados
              operacionais necessários para agenda, caixa, pedidos e
              atendimento.
            </p>
            <p>
              Quando um salão autoriza a integração com o Instagram, a
              plataforma trata apenas os dados necessários para conectar a conta
              profissional, receber mensagens enviadas a ela e permitir que o
              agente configurado pelo próprio salão responda ao contato.
            </p>
          </PolicySection>

          <PolicySection title="Finalidades do tratamento">
            <div className="grid gap-3 sm:grid-cols-2">
              <p className="rounded-xl bg-slate-50 p-3">
                <Database className="mb-2 h-4 w-4 text-emerald-700" />
                Operar agendamentos, pedidos, pagamentos e rotinas
                administrativas.
              </p>
              <p className="rounded-xl bg-slate-50 p-3">
                <MessageCircle className="mb-2 h-4 w-4 text-emerald-700" />
                Atender mensagens recebidas pelas contas profissionais que o
                salão conectou.
              </p>
              <p className="rounded-xl bg-slate-50 p-3">
                <ShieldCheck className="mb-2 h-4 w-4 text-emerald-700" />
                Proteger acessos, prevenir uso indevido e registrar ações
                operacionais relevantes.
              </p>
              <p className="rounded-xl bg-slate-50 p-3">
                <UserRound className="mb-2 h-4 w-4 text-emerald-700" />
                Permitir que cada administrador visualize somente os dados da
                própria unidade autorizada.
              </p>
            </div>
          </PolicySection>

          <PolicySection title="Integração com Instagram">
            <p>
              O salão inicia a conexão e autoriza a conta profissional no fluxo
              oficial do Instagram. A autorização é individual por salão. A
              plataforma usa as permissões concedidas apenas para identificar a
              conta profissional conectada e gerenciar as mensagens recebidas
              por ela.
            </p>
            <p>
              Não vendemos dados de mensagens, não acessamos conversas de contas
              não conectadas e não compartilhamos o conteúdo de uma unidade com
              outra. A conta pode ser desconectada pela administração do salão a
              qualquer momento nas configurações de integração.
            </p>
          </PolicySection>

          <PolicySection title="Armazenamento, segurança e compartilhamento">
            <p>
              Os dados são mantidos pelo período necessário para oferecer a
              plataforma, atender obrigações operacionais e legais aplicáveis,
              resolver solicitações e manter a segurança. O acesso é limitado
              por autenticação, perfis de acesso e isolamento por salão.
            </p>
            <p>
              Dados podem ser transmitidos a provedores necessários para a
              funcionalidade solicitada pelo salão, como Meta/Instagram, serviço
              de mensagens, meios de pagamento ou armazenamento. Cada integração
              só é utilizada depois da configuração ou autorização
              correspondente.
            </p>
          </PolicySection>

          <PolicySection title="Seus direitos e contato">
            <p>
              Você pode solicitar informações, correção, exportação, revogação
              de autorizações aplicáveis ou exclusão de dados sujeitos a essa
              solicitação. Para instruções específicas de exclusão, acesse{" "}
              <a
                className="font-medium text-emerald-800 underline underline-offset-2"
                href="/exclusao-de-dados"
              >
                a página de exclusão de dados
              </a>
              .
            </p>
            <p>
              Para falar sobre privacidade, envie um e-mail para{" "}
              <a
                className="font-medium text-emerald-800 underline underline-offset-2"
                href={`mailto:${CONTACT_EMAIL}?subject=Privacidade%20-%20Beleza%20e%20Bronze`}
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </PolicySection>

          <section className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-sm leading-6 text-emerald-950">
            <h2 className="font-semibold">
              English summary for platform review
            </h2>
            <p className="mt-2">
              Beleza e Bronze is a multi-tenant management platform for salons.
              Each salon independently authorizes its own professional Instagram
              account. We use Instagram data only to identify the authorized
              professional account and to receive and manage messages sent to
              that account. Data and access are isolated by salon. We do not
              sell message data or share it across salons. Data deletion
              instructions are available at{" "}
              <a
                className="font-medium underline underline-offset-2"
                href="/exclusao-de-dados"
              >
                /exclusao-de-dados
              </a>
              .
            </p>
          </section>
        </div>

        <footer className="py-8 text-center text-xs text-slate-500">
          <a
            className="inline-flex items-center gap-1 hover:text-slate-800"
            href={`mailto:${CONTACT_EMAIL}?subject=Privacidade%20-%20Beleza%20e%20Bronze`}
          >
            <Mail className="h-3.5 w-3.5" />
            Contato de privacidade
          </a>
        </footer>
      </div>
    </main>
  );
}
